import { Injectable, NgZone } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
    private readonly activityEvents: Array<keyof WindowEventMap> = [
        'click',
        'mousemove',
        'keydown',
        'scroll',
        'touchstart'
    ];
    private timeoutHandle: number | null = null;
    private inactivityMs = 15 * 60 * 1000;
    private tracking = false;
    private timeoutCallback: (() => void) | null = null;
    private readonly fallbackMinutes = 15;

    constructor(private zone: NgZone) {}

    start(minutes: number, callback: () => void): void {
        if (typeof window === 'undefined') {
            return;
        }

        this.stop();
        const normalizedMinutes = this.sanitizeMinutes(minutes);
        if (normalizedMinutes === 0) {
            return;
        }
        this.inactivityMs = normalizedMinutes * 60 * 1000;
        this.timeoutCallback = callback;
        this.tracking = true;

        this.zone.runOutsideAngular(() => {
            this.activityEvents.forEach((event) =>
                window.addEventListener(event, this.handleActivity, true)
            );
            this.resetTimer();
        });
    }

    notifyExternalActivity(): void {
        if (!this.tracking) {
            return;
        }
        this.resetTimer();
    }

    stop(): void {
        if (typeof window === 'undefined') {
            return;
        }

        this.tracking = false;
        this.activityEvents.forEach((event) =>
            window.removeEventListener(event, this.handleActivity, true)
        );

        if (this.timeoutHandle) {
            window.clearTimeout(this.timeoutHandle);
            this.timeoutHandle = null;
        }

        this.timeoutCallback = null;
    }

    private handleActivity = (): void => {
        this.resetTimer();
    };

    private resetTimer(): void {
        if (!this.tracking) {
            return;
        }

        if (this.timeoutHandle) {
            window.clearTimeout(this.timeoutHandle);
        }

        this.timeoutHandle = window.setTimeout(() => {
            this.zone.run(() => this.triggerTimeout());
        }, this.inactivityMs);
    }

    private triggerTimeout(): void {
        if (!this.timeoutCallback) {
            return;
        }

        const callback = this.timeoutCallback;
        this.stop();
        callback();
    }

    private sanitizeMinutes(minutes: number): number {
        if (!Number.isFinite(minutes)) {
            return this.fallbackMinutes;
        }
        if (minutes < 0) {
            return this.fallbackMinutes;
        }
        return minutes;
    }
}
